import toast from 'react-hot-toast';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { useLocation } from 'react-router';
import useMounted from '../../../hooks/useMounted';
import { useCallback, useEffect, useState } from 'react';
import { usersApi } from '../../../api/usersApi';
import { rolesApi, toRoleOptions, currentRoleValue } from '../../../api/rolesApi';
import PhotoUploadDialog from './PhotoUploadDialog';
import useAuth from '../../../hooks/useAuth';

const UserFunctionSettings = (props) => {
  const path = useLocation();
  const { user } = useAuth();
  const routeUserId = path.pathname.split('/')[3];
  const effectiveUserId = props.userId || routeUserId || (user ? user.id : null);
  const [userToUpdate, setUserToUpdate] = useState({});
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  // On choisit un rôle au lieu du couple niveau d'accès / service. Avant la
  // migration, la liste propose les rôles qui seront créés (voir toRoleOptions).
  // null = API des rôles indisponible : on garde les deux listes historiques.
  const [roleOptions, setRoleOptions] = useState(null);
  const [roleValue, setRoleValue] = useState('');
  const isSelf = user && String(user.id) === String(effectiveUserId);
  const mounted = useMounted();

  useEffect(() => {
    rolesApi.getRoles()
      .then((res) => {
        if (mounted.current) setRoleOptions(toRoleOptions(res));
      })
      .catch(() => {
        if (mounted.current) setRoleOptions(null);
      });
  }, [mounted]);

  useEffect(() => {
    setRoleValue(currentRoleValue(roleOptions, userToUpdate));
  }, [roleOptions, userToUpdate.id, userToUpdate.role_id, userToUpdate.access_level, userToUpdate.id_service]);

  const getUserInfos = useCallback(async () => {
    try {
      if (!effectiveUserId) return;
      const data = await usersApi.getUserInfos(window.localStorage.getItem('accessToken'), effectiveUserId);
      if (mounted.current) {
        setUserToUpdate(data.data);
      }
    } catch (err) {
      console.error(err);
    }
  }, [mounted, effectiveUserId]);

  const handleChange = (field) => (event) => {
    setUserToUpdate(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  useEffect(() => {
    getUserInfos();
  }, [getUserInfos]);

  return (
    <>
      <Grid
        container
        spacing={3}
        sx={{ mt: 1 }}
        {...props}
      >
        <Grid
          item
          lg={4}
          md={6}
          xl={3}
          xs={12}
        >
          <Card>
            <CardContent>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'center'
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    border: (theme) => `1px dashed ${theme.palette.divider}`,
                    borderRadius: '50%'
                  }}
                >
                  <Avatar
                    src={userToUpdate.photo ? `data:image/jpeg;base64,${userToUpdate.photo}` : ''}
                    sx={{
                      height: 140,
                      width: 140
                    }}
                  />
                </Box>
                <Typography
                  color="textPrimary"
                  sx={{ mt: 1 }}
                  variant="subtitle2"
                >
                  {userToUpdate.firstname}
                  {' '}
                  {userToUpdate.lastname}
                </Typography>
                <Typography
                  color="textSecondary"
                  variant="body2"
                >
                  {userToUpdate.functions}
                </Typography>
              </Box>
            </CardContent>
            <CardActions>
              <Button
                color="primary"
                fullWidth
                variant="text"
                onClick={() => setPhotoDialogOpen(true)}
              >
                Changer de photo
              </Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid
          item
          lg={8}
          md={6}
          xl={9}
          xs={12}
        >
          <Card>
            <CardHeader title="Fonction" />
            <Divider />
            <CardContent>
              <Grid
                container
                spacing={4}
              >
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    fullWidth
                    label="Fonction"
                    value={userToUpdate.functions || ''}
                    onChange={handleChange('functions')}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {roleOptions ? (
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    select
                    fullWidth
                    label="Rôle"
                    value={roleValue}
                    onChange={(event) => setRoleValue(event.target.value)}
                    disabled={isSelf}
                    helperText={isSelf ? 'Vous ne pouvez pas changer votre propre rôle.' : 'Permissions de chaque rôle : Paramètres > Rôles & permissions.'}
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="" disabled>
                      <em>Aucun rôle</em>
                    </MenuItem>
                    {roleOptions.options.map((role) => (
                      <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                ) : (
                <>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Niveau d'accès</InputLabel>
                    <Select
                      value={userToUpdate.access_level ?? ''}
                      onChange={handleChange('access_level')}
                      label="Niveau d'accès"
                    >
                      <MenuItem value={0}>Fort (0)</MenuItem>
                      <MenuItem value={1}>Faible (1)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Service</InputLabel>
                    <Select
                      value={userToUpdate.id_service ?? ''}
                      onChange={handleChange('id_service')}
                      label="Service"
                    >
                      <MenuItem value={0}>Administrateur (0)</MenuItem>
                      <MenuItem value={3}>Commercial (3)</MenuItem>
                      <MenuItem value={4}>Relance (4)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                </>
                )}
              </Grid>
            </CardContent>
            <Divider />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                p: 2
              }}
            >
              <Button
                color="primary"
                variant="contained"
                onClick={async () => {
                  try {
                    // Le rôle choisi impose le niveau d'accès et le service. Avant la
                    // migration, seul ce couple est enregistré (pas de role_id).
                    const role = roleOptions?.options.find((r) => r.value === roleValue) || null;
                    const updateData = {
                      functions: userToUpdate.functions,
                      access_level: role ? role.access_level : parseInt(userToUpdate.access_level),
                      id_service: role ? role.id_service : parseInt(userToUpdate.id_service),
                      role_id: role && roleOptions.ready ? role.value : undefined
                    };
                    if (!effectiveUserId) return;
                    const res = await usersApi.updateUserFunction(window.localStorage.getItem('accessToken'), updateData, effectiveUserId);
                    if (res && res.status && res.status >= 400) {
                      toast.error(res.message || 'Erreur lors de la mise à jour');
                      return;
                    }
                    toast.success('Fonction mise à jour !');
                  } catch (err) {
                    console.error(err);
                    toast.error('Erreur lors de la mise à jour');
                  }
                }}
              >
                Enregistrer les modifications
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <PhotoUploadDialog
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        onPhotoUploaded={(photoData) => {
          setUserToUpdate(prev => ({
            ...prev,
            photo: photoData.split(',')[1] // Enlever le préfixe data:image/...
          }));
        }}
        currentPhoto={userToUpdate.photo ? `data:image/jpeg;base64,${userToUpdate.photo}` : ''}
        userId={effectiveUserId}
      />
    </>
  );
};

export default UserFunctionSettings;
